import { ChevronLeft, ChevronRight } from "lucide-react";
import type * as React from "react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/v2/ui/primitives/button";
import { dateFnsLocale } from "@/v2/utils/format-date";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      // Localizes weekday headers, ARIA labels and week-start. Spread last so an
      // explicit caller-provided locale still wins. Cast bridges a types-only
      // skew: react-day-picker v9 ships date-fns v4-shaped Locale types but
      // resolves the root date-fns v2 at runtime, so the objects match in fact.
      locale={dateFnsLocale() as unknown as CalendarProps["locale"]}
      fixedWeeks={true}
      showOutsideDays={showOutsideDays}
      className={cn("tw-p-3", className)}
      classNames={{
        months:
          "tw-relative tw-flex tw-flex-col sm:tw-flex-row tw-space-y-4 sm:tw-space-x-4 sm:tw-space-y-0",
        month: "tw-space-y-4",
        month_caption:
          "tw-flex tw-justify-center tw-pt-1 tw-relative tw-items-center",
        caption_label: "tw-text-sm tw-font-medium",
        nav: "tw-absolute tw-top-0 tw-flex tw-w-full tw-justify-between tw-items-center tw-px-1 tw-pt-1 tw-z-10",
        button_previous:
          "tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-input tw-bg-transparent tw-h-7 tw-w-7 tw-p-0 tw-opacity-50 hover:tw-opacity-100 hover:tw-bg-accent tw-transition-all tw-duration-200 tw-text-foreground",
        button_next:
          "tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-input tw-bg-transparent tw-h-7 tw-w-7 tw-p-0 tw-opacity-50 hover:tw-opacity-100 hover:tw-bg-accent tw-transition-all tw-duration-200 tw-text-foreground",
        chevron: "tw-h-4 tw-w-4 tw-text-foreground",
        month_grid: "tw-w-full tw-border-collapse tw-space-y-1",
        weekdays: "tw-flex",
        weekday:
          "tw-text-muted-foreground tw-rounded-md tw-w-9 tw-font-normal tw-text-[0.8rem] tw-text-center",
        week: "tw-flex tw-w-full tw-mt-2",
        day: "tw-h-9 tw-w-9 tw-text-center tw-text-sm tw-p-0 tw-relative [&:has([aria-selected].day-range-end)]:tw-rounded-r-md [&:has([aria-selected].day-outside)]:tw-bg-accent/50 [&:has([aria-selected])]:tw-bg-accent first:[&:has([aria-selected])]:tw-rounded-l-md last:[&:has([aria-selected])]:tw-rounded-r-md focus-within:tw-relative focus-within:tw-z-20",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "tw-h-9 tw-w-9 tw-p-0 tw-font-normal aria-selected:tw-opacity-100"
        ),
        range_end: "day-range-end",
        selected:
          "tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary hover:tw-text-primary-foreground focus:tw-bg-primary focus:tw-text-primary-foreground",
        today: "tw-bg-accent tw-text-accent-foreground",
        outside:
          "day-outside tw-text-muted-foreground tw-opacity-50 aria-selected:tw-bg-accent/50 aria-selected:tw-text-accent-foreground aria-selected:tw-opacity-30",
        disabled: "tw-text-muted-foreground tw-opacity-50",
        range_middle:
          "aria-selected:tw-bg-accent aria-selected:tw-text-accent-foreground",
        hidden: "tw-invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeft className="tw-h-4 tw-w-4" />
          ) : (
            <ChevronRight className="tw-h-4 tw-w-4" />
          ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
